import dayjs from 'dayjs';
import { Table, Tag } from '@maxtropy/components';
import { ValveStatus } from '@/pages/GasSchedule/types';
import { valveStatusTagColorMap } from '@/pages/GasSchedule/utils';
import {
  apiV2GasMonitorMapGasSupplySchedulePost,
  V2GasMonitorMapGasSupplySchedulePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';

type GasScheduleApiRow = Exclude<V2GasMonitorMapGasSupplySchedulePostResponse['list'], undefined>[number];
type GasScheduleTableRow = GasScheduleApiRow & { __scrollIndex: number };

export const valveStatusTextMap: Record<ValveStatus, string> = {
  [ValveStatus.Open]: '开启',
  [ValveStatus.Close]: '关闭',
  [ValveStatus.Offline]: '离线',
};
function getRowKey(record: GasScheduleApiRow) {
  return `${record.id}-${record.mediumId}-${record.deviceId}`;
}

// 固定滚动速度：每秒滚动 30px
const SCROLL_SPEED = 30;
const TABLE_SCROLL_INTERVAL = 10000;
const TABLE_SCROLL_DURATION = 800;

const ScrollText: React.FC<{ text: string }> = ({ text }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [duration, setDuration] = useState(20);

  useEffect(() => {
    if (spanRef.current) {
      const width = spanRef.current.scrollWidth / 2; // 因为复制了一份，实际宽度是单份
      // 动画时长 = 内容宽度 / 滚动速度
      const newDuration = Math.max(width / SCROLL_SPEED, 5);
      setDuration(newDuration);
    }
  }, [text]);

  return (
    <div className={styles.scrollText}>
      <span ref={spanRef} style={{ animationDuration: `${duration}s` }}>
        {text}
        <span style={{ paddingLeft: 50 }}>{text}</span>
      </span>
    </div>
  );
};

const GasSchedule = () => {
  // 供气日程表数据
  const { data, loading } = useRequest(() =>
    apiV2GasMonitorMapGasSupplySchedulePost({}).then(res => (res.list ?? []).filter(k => !!k.valveStatus))
  );
  const scheduleTableRef = useRef<HTMLDivElement>(null);
  const [tableData, setTableData] = useState<GasScheduleTableRow[]>([]);
  const [rowHeight, setRowHeight] = useState(40);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (!data?.length) {
      setTableData([]);
      return;
    }

    setTableData(data.map((item, index) => ({ ...item, __scrollIndex: index })));
  }, [data]);

  useEffect(() => {
    if (loading || tableData.length <= 1) {
      return;
    }

    let timer: number | undefined;
    let animationTimer: number | undefined;

    timer = window.setInterval(() => {
      const firstRow = scheduleTableRef.current?.querySelector<HTMLTableRowElement>('.ant-table-tbody > tr');
      const nextRowHeight = firstRow?.getBoundingClientRect().height;

      if (nextRowHeight) {
        setRowHeight(nextRowHeight);
      }

      setIsScrolling(true);

      animationTimer = window.setTimeout(() => {
        setTableData(prev => {
          if (prev.length <= 1) {
            return prev;
          }

          const [first, ...rest] = prev;
          return [...rest, first];
        });
        setIsScrolling(false);
      }, TABLE_SCROLL_DURATION);
    }, TABLE_SCROLL_INTERVAL);

    return () => {
      if (timer) {
        window.clearInterval(timer);
      }
      if (animationTimer) {
        window.clearTimeout(animationTimer);
      }
    };
  }, [loading, tableData.length]);

  const columns = [
    {
      title: '供气区域',
      dataIndex: 'zoneName',
      width: 100,
      render: (v: string) => (
        <span className={styles.zoneName} title={v}>
          {v || '--'}
        </span>
      ),
    },
    {
      title: '供气管道阀门',
      dataIndex: 'deviceName',
      width: 110,
      render: (v: string) => <ScrollText text={v ?? '--'} />,
    },
    {
      title: '供气时间',
      dataIndex: 'timeDetailVoList',
      width: 110,
      render: (_: unknown, record: GasScheduleApiRow) => {
        const timeText = (record.timeDetailVoList ?? [])
          .map(timeDetail => {
            const start = timeDetail.startDateTime ? dayjs(timeDetail.startDateTime).format('HH:mm') : '--';
            const end = timeDetail.endDateTime ? dayjs(timeDetail.endDateTime).format('HH:mm') : '--';
            return `${start}~${end}`;
          })
          .join('、');
        return <ScrollText text={timeText} />;
      },
    },
    {
      title: '状态',
      dataIndex: 'valveStatus',
      render: (value?: ValveStatus) => {
        if (!value) {
          return '--';
        }
        return (
          <Tag color={valveStatusTagColorMap[value]} border="solid">
            {valveStatusTextMap[value]}
          </Tag>
        );
      },
    },
  ];

  return (
    <div
      ref={scheduleTableRef}
      className={`${styles.scheduleTable} ${isScrolling ? styles.scrolling : ''}`}
      style={{ '--schedule-row-height': `${rowHeight}px` } as CSSProperties}
    >
      <Table
        rowKey={(record: GasScheduleTableRow) => `${getRowKey(record)}-${record.__scrollIndex}`}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ y: 280 }}
        bordered={false}
        loading={loading}
      />
    </div>
  );
};

export default GasSchedule;
